import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  forceReLogin,
  getAuthToken,
  hasUsableAccessToken,
  login as startLogin,
  logout as startLogout,
  msUntilTokenRefresh,
  readTokenClaims,
  refreshAccessToken,
} from "@/lib/auth/authService";
import { ACCESS_TOKEN, getCookieByKey, REFRESH_TOKEN, setSecureCookie } from "@/lib/auth/cookieUtils";
import {
  getUserProfile,
  type UserProfileData,
} from "@/lib/auth/userProfileService";

type AuthContextType = {
  userProfile: UserProfileData | null;
  displayName: string;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ALLOWED_ORIGINS = () =>
  (window.APP_CONFIG?.PUBLIC_ALLOWED_ORIGINS || "")
    .split(",")
    .map((o) => o.trim())
    .filter(Boolean);

function isOriginAllowed(origin: string): boolean {
  if (window.location.hostname === "localhost") return true;
  const allowed = ALLOWED_ORIGINS();
  return allowed.length === 0 || allowed.includes(origin);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokenReady, setTokenReady] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshAuth = useCallback(async (options?: { quiet?: boolean }) => {
    const quiet = options?.quiet === true;

    if (!hasUsableAccessToken()) {
      const token = getAuthToken();
      if (token) {
        // Access token expired — try refresh before treating as logged out.
        const ok = await refreshAccessToken();
        if (!ok) {
          setUserProfile(null);
          setTokenReady(false);
          setIsLoading(false);
          forceReLogin();
          return;
        }
      } else {
        setUserProfile(null);
        setTokenReady(false);
        setIsLoading(false);
        return;
      }
    }

    setTokenReady(true);
    // Only gate the UI on the initial auth check — quiet refreshes must not
    // flip isLoading or AuthGate unmounts /configuration every cycle.
    if (!quiet) setIsLoading(true);
    try {
      const response = await getUserProfile();
      setUserProfile(response.data);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        const ok = await refreshAccessToken();
        if (ok) {
          try {
            const retry = await getUserProfile();
            setUserProfile(retry.data);
            return;
          } catch {
            /* fall through */
          }
        }
        setUserProfile(null);
        setTokenReady(false);
        forceReLogin();
      } else {
        // Profile APIM may be unavailable locally — still treat JWT as logged in.
        setUserProfile(null);
      }
    } finally {
      if (!quiet) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  // Renew access token before EXPIRES_TIME (skew is proportional to lifetime).
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const schedule = () => {
      if (cancelled) return;
      if (!getCookieByKey(REFRESH_TOKEN)) return;

      const ms = msUntilTokenRefresh();
      // Past-due / missing: retry soon but not on a tight 5s loop forever.
      const delay =
        ms == null ? 60_000 : Math.min(Math.max(ms, 30_000), 30 * 60_000);

      timeoutId = setTimeout(() => {
        void (async () => {
          const ok = await refreshAccessToken();
          if (cancelled) return;
          if (ok) {
            void refreshAuth({ quiet: true });
            schedule();
            return;
          }
          forceReLogin();
        })();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [refreshAuth, tokenReady]);

  useEffect(() => {
    const isInIframe = window.self !== window.top;
    if (!isInIframe) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const handler = (event: MessageEvent): void => {
      if (!isOriginAllowed(event.origin)) return;

      const data = event.data as { type?: string; token?: unknown };
      if (data?.type === "AUTH_TOKEN" && data.token) {
        clearTimeout(timeoutId);
        const raw = data.token;
        const tokenStr: string =
          typeof raw === "string"
            ? raw
            : String(
                (raw as { access_token?: string; token?: string })
                  ?.access_token ??
                  (raw as { token?: string })?.token ??
                  raw,
              );
        setSecureCookie(ACCESS_TOKEN, tokenStr);
        void refreshAuth();
      }
    };

    window.addEventListener("message", handler);

    if (window.parent !== window) {
      window.parent.postMessage({ type: "APP_READY" }, "*");
      timeoutId = setTimeout(() => {
        if (!getAuthToken()) {
          console.warn(
            "No AUTH_TOKEN from parent and no session. Falling back to login.",
          );
          void startLogin();
        }
      }, 10_000);
    }

    return () => {
      window.removeEventListener("message", handler);
      clearTimeout(timeoutId);
    };
  }, [refreshAuth]);

  const isAuthenticated = tokenReady || hasUsableAccessToken();

  const displayName = useMemo(() => {
    if (userProfile?.profile?.displayName) return userProfile.profile.displayName;
    if (userProfile?.profile?.email) return userProfile.profile.email;
    const claims = readTokenClaims();
    return claims.name || claims.email || claims.sub || "Signed in";
  }, [userProfile]);

  const value = useMemo<AuthContextType>(
    () => ({
      userProfile,
      displayName,
      isLoading,
      isAuthenticated,
      login: () => {
        void startLogin();
      },
      logout: () => startLogout("/dashboard"),
    }),
    [userProfile, displayName, isLoading, isAuthenticated],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
